import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useComplianceDashboard } from '@/api/hooks/useComplianceDashboard';
import { useAuthStore } from '@/store/auth.store';
import { ComplianceHeroTile } from '@/components/dashboards/ComplianceHeroTile';
import { ComplianceCountTile } from '@/components/dashboards/ComplianceCountTile';
import { FrameworkScorecard } from '@/components/dashboards/FrameworkScorecard';
import { ControlStatusBars } from '@/components/dashboards/ControlStatusBars';
import { AssignmentCoveragePanel } from '@/components/dashboards/AssignmentCoveragePanel';
import { EvidenceHealthPanel } from '@/components/dashboards/EvidenceHealthPanel';
import { ExceptionsByFrameworkPanel } from '@/components/dashboards/ExceptionsByFrameworkPanel';
import { ComplianceDashboardEmptyState } from '@/components/dashboards/ComplianceDashboardEmptyState';
import { LastUpdatedIndicator } from '@/components/dashboards/LastUpdatedIndicator';

// H2 tooltip per D-DASH.3-PA-PE-2
const H2_TOOLTIP = 'Controls with a terminal status: implemented, compensating control, or not applicable.';

function pct(numerator: number, denominator: number): number {
  return denominator > 0 ? Math.round((numerator / denominator) * 100) : 0;
}

export function ComplianceDashboardPage() {
  const { hasAnyRole } = useAuthStore();
  const isCiso = hasAnyRole(['risk_manager', 'super_admin']);

  const { data, isLoading, isFetching, isError, refetch } = useComplianceDashboard();

  useEffect(() => {
    document.title = 'Compliance Dashboard — VeriGRC';
  }, []);

  const isEmpty = !isLoading && data != null && data.frameworks.length === 0;
  // ⚠ RET-10: a REAL error branch. Before, a failed load (no data) fell through to the tile layout with zeros — a
  //   wrong statement about the program, not a retrieval problem. Shown only when there is nothing loaded to show.
  const isLoadError = isError && data == null;

  const openWorkDrillDown = isCiso
    ? '/compliance/workload'
    : '/compliance/my-assignments?status=in_progress';

  const frameworkCount = data?.frameworks.length ?? 0;
  const evidence = data?.evidence;
  // Hero coverage is derived from the same numerator/denominator shown in the subtitle so the tile
  // is internally consistent ("2 of 4" → 50%), never a weighted figure that could disagree.
  const evidenceCoveragePct = evidence ? pct(evidence.with_evidence, evidence.implemented) : 0;
  const evidenceExpiring = evidence ? evidence.expired + evidence.expiring : 0;
  const needAttention = (data?.total_controls ?? 0) - (data?.controls_addressed ?? 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-xl font-semibold text-text-primary">Compliance Dashboard</h1>
        <p className="text-sm text-text-secondary">Compliance program health overview</p>
      </div>

      {isLoadError ? (
        <Card data-testid="compliance-dashboard-error">
          <CardContent className="flex flex-col items-center gap-[7px] px-6 py-[52px] text-center">
            <span className="text-[13px] font-semibold text-text-muted">The compliance dashboard could not be loaded</span>
            <span className="max-w-[44ch] text-[12px] leading-[1.6] text-text-faint">
              This is a problem retrieving your data, not a statement about it.
            </span>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => refetch()} disabled={isFetching}>
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : isEmpty ? (
        <ComplianceDashboardEmptyState />
      ) : (
        <>
          {/* Hero section — H1 H2 H3 */}
          <section aria-label="Key compliance metrics" data-testid="hero-section">
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              <ComplianceHeroTile
                label="Readiness"
                value={data?.avg_readiness_pct ?? 0}
                unit="%"
                tone="cyan"
                barPct={data?.avg_readiness_pct ?? 0}
                subtitle={`Weighted by controls in scope across ${frameworkCount} framework${frameworkCount === 1 ? '' : 's'}`}
                addNote={`${data?.controls_addressed ?? 0} of ${data?.total_controls ?? 0} controls are complete.`}
                drillDownPath="/compliance"
                drillDownLabel="View frameworks"
                isLoading={isLoading}
                data-testid="tile-readiness"
              />
              <ComplianceHeroTile
                label="Controls Addressed"
                value={`${data?.controls_addressed ?? 0} / ${data?.total_controls ?? 0}`}
                tone="cyan"
                barPct={pct(data?.controls_addressed ?? 0, data?.total_controls ?? 0)}
                subtitle="Implemented + compensating + not applicable"
                tooltip={H2_TOOLTIP}
                addNote={`${needAttention} control${needAttention === 1 ? '' : 's'} still need attention.`}
                drillDownPath="/compliance"
                drillDownLabel="View controls"
                isLoading={isLoading}
                data-testid="tile-controls-addressed"
              />
              <ComplianceHeroTile
                label="Evidence Coverage"
                value={evidenceCoveragePct}
                unit="%"
                tone="green"
                barPct={evidenceCoveragePct}
                subtitle={`${evidence?.with_evidence ?? 0} of ${evidence?.implemented ?? 0} completed controls have evidence records.`}
                addNote="Evidence records track metadata and approved evidence locations. No record means evidence has not been documented yet."
                drillDownPath="/compliance/evidence"
                drillDownLabel="View evidence"
                isLoading={isLoading}
                data-testid="tile-evidence-coverage"
              />
            </div>
          </section>

          {/* Secondary section — count tiles */}
          <section aria-label="Supporting compliance metrics" data-testid="secondary-section">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
              <ComplianceCountTile
                label="Open Work Queue"
                count={data?.open_work_count ?? 0}
                subtitle="Controls awaiting completion"
                drillDownPath={openWorkDrillDown}
                drillDownLabel={isCiso ? 'View workload' : 'View my tasks'}
                isLoading={isLoading}
                data-testid="tile-open-work"
              />
              <ComplianceCountTile
                label="Framework Exceptions"
                count={data?.open_exceptions_count ?? 0}
                subtitle={(data?.open_exceptions_count ?? 0) > 0 ? 'Approved active exceptions' : 'No approved active exceptions'}
                countColor={(data?.open_exceptions_count ?? 0) > 0 ? undefined : '#5C7189'}
                drillDownPath="/compliance/exceptions"
                drillDownLabel="View exceptions"
                isLoading={isLoading}
                data-testid="tile-open-exceptions"
              />
              <ComplianceCountTile
                label="Controls Needing Attention"
                count={data?.controls_needing_attention ?? 0}
                subtitle="Unassigned or assigned only"
                countColor="#F0C674"
                drillDownPath="/compliance"
                drillDownLabel="View controls"
                isLoading={isLoading}
                data-testid="tile-needing-attention"
              />
              <ComplianceCountTile
                label="Evidence Expiring"
                count={evidenceExpiring}
                subtitle={evidenceExpiring > 0 ? `${evidence?.expired ?? 0} expired · ${evidence?.expiring ?? 0} within 30 days` : 'None expiring'}
                countColor={evidenceExpiring > 0 ? '#F09E99' : '#5C7189'}
                accentBorder
                drillDownPath="/compliance/evidence"
                drillDownLabel="View evidence"
                isLoading={isLoading}
                data-testid="tile-evidence-expiring"
              />
            </div>
          </section>

          {/* Framework scorecard */}
          <FrameworkScorecard data={data?.frameworks ?? []} isLoading={isLoading} />

          {/* 2×2 analysis panels */}
          <section aria-label="Compliance detail panels" data-testid="panels-section">
            <div className="grid grid-cols-1 items-stretch gap-3 lg:grid-cols-2">
              <ControlStatusBars data={data?.status_distribution ?? []} isLoading={isLoading} />
              <AssignmentCoveragePanel data={data?.assignment_coverage ?? []} isLoading={isLoading} />
              <EvidenceHealthPanel
                evidence={evidence ?? { records: 0, expired: 0, expiring: 0, without: 0, with_evidence: 0, implemented: 0 }}
                isLoading={isLoading}
              />
              <ExceptionsByFrameworkPanel frameworks={data?.frameworks ?? []} isLoading={isLoading} />
            </div>
          </section>
        </>
      )}

      <LastUpdatedIndicator
        generatedAt={data?.generated_at}
        onRefresh={() => refetch()}
        isRefreshing={isFetching}
      />
    </div>
  );
}
